/**
 * <LevelBars> — un niveau sonore en barres (atom).
 *
 * Le micro qui entend, le son qui joue : au test du matériel, l'élève doit VOIR que quelque chose
 * bouge quand il dit « allô » ou quand la mélodie part. Des barres de hauteur croissante ; celles
 * que le niveau atteint s'allument à la couleur d'affichage de l'univers (jamais un texte), les
 * autres restent au contour.
 *
 * Contrôlé et immobile : le niveau (0 à 1) arrive en prop, l'écran le relit à son rythme (le micro
 * dix fois par seconde aujourd'hui). Rien ne s'anime ici.
 *
 * `size="sm"` : les barres d'un repère posé sur une vidéo, à la hauteur d'un nom (la connexion faible d'un élève du
 * live du masteur, les barres de l'ancienne tuile : une seule allumée sur trois, à l'orange d'alerte — `color`).
 */
import React from "react"
import { View } from "react-native"
import { StyleSheet } from "react-native-unistyles"

import { type SemanticColorKey } from "../../theme/tokens/colors"

export type LevelBarsProps = {
    /** Le niveau, de 0 (rien) à 1 (toutes les barres). */
    level: number
    /** Le nombre de barres. Défaut 6, celui d'aujourd'hui. */
    count?: number
    /** `md` (défaut) : le test du matériel ; `sm` : un repère de 12 px de haut, sur une vidéo. */
    size?: "sm" | "md"
    /** La couleur des barres allumées. Défaut `role`, l'affichage de l'univers. */
    color?: SemanticColorKey
    /** Ce que le lecteur d'écran dit des barres. */
    accessibilityLabel?: string
    testID?: string
}

/**
 * La barre la plus basse, puis le pas de l'une à l'autre : en `md`, la plus haute fait 30, la hauteur
 * d'aujourd'hui ; en `sm`, trois barres tiennent dans les 12 px d'un glyphe de nom.
 */
const BAR = {
    md: { width: 6, minHeight: 8, step: 4.4 },
    sm: { width: 3, minHeight: 4, step: 4 },
}

export function LevelBars(props: LevelBarsProps) {
    const { level, count = 6, size = "md", color = "role", accessibilityLabel, testID } = props
    const bar = BAR[size]
    const lit = Math.round(Math.max(0, Math.min(1, level)) * count)

    return (
        <View
            style={styles.row(size)}
            testID={testID}
            accessible={accessibilityLabel != null}
            accessibilityLabel={accessibilityLabel}
        >
            {Array.from({ length: count }, (_, index) => (
                <View
                    key={index}
                    style={styles.bar(bar.width, bar.minHeight + index * bar.step, index < lit ? color : null)}
                />
            ))}
        </View>
    )
}

const styles = StyleSheet.create((theme) => ({
    /** L'écart du test du matériel ; en `sm`, la barre de 3 px garde la moitié de sa largeur de jour. */
    row: (size: "sm" | "md") => ({
        flexDirection: "row" as const,
        alignItems: "flex-end" as const,
        gap: size === "md" ? theme.spacing["1"] : BAR.sm.width / 2,
    }),
    bar: (width: number, height: number, lit: SemanticColorKey | null) => ({
        width,
        height,
        borderRadius: theme.radius.full,
        backgroundColor: lit != null ? theme.colors[lit] : theme.colors.border,
    }),
}))
